import { trackEvent } from "@/utils/analytics";
import { BlogPost } from "@/utils/blogUtils";
import { Check, Facebook, Link2, Linkedin, Share2, X } from "lucide-react";
import { useState } from "react";
import "./BlogPostSocialShareStyles.css";
import ShareTooltip from "./ShareTooltip";

interface BlogPostSocialShareProps {
  post: BlogPost;
  handleSocialShare: (platform: string) => void;
}

const BlogPostSocialShare = ({ post, handleSocialShare }: BlogPostSocialShareProps) => {
  const [linkCopied, setLinkCopied] = useState(false);

  // Prepare sharing text with appealing messages in Brazilian Portuguese
  const facebookShareText = `Informação valiosa para processos judiciais! Confira este artigo sobre "${post.title}" que pode ajudar no seu caso.`;
  const twitterShareText = `Conhecimento essencial para sua defesa judicial! Leia sobre "${post.title}" neste artigo especializado. #PericiaJudicial #EngenhariaLegal`;
  const linkedinShareText = `Compartilho este artigo técnico sobre "${post.title}" que pode ser fundamental para profissionais envolvidos em processos judiciais. Informação de qualidade faz diferença!`;
  const whatsappShareText = `Olá! Encontrei este artigo sobre "${post.title}" que pode ser muito útil para seu caso judicial. Confira: https://www.periciatecnica.eng.br/blog/${post.slug}`;

  // Tooltip messages
  const tooltips = {
    facebook: "Compartilhar no Facebook",
    twitter: "Compartilhar no X",
    linkedin: "Compartilhar no LinkedIn",
    whatsapp: "Compartilhar no WhatsApp",
    native: "Compartilhar este artigo",
    copyLink: linkCopied ? "Link copiado!" : "Copiar link do artigo"
  };

  // Handle copy link
  const handleCopyLink = () => {
    const url = `https://www.periciatecnica.eng.br/blog/${post.slug}`;
    navigator.clipboard.writeText(url).then(() => {
      setLinkCopied(true);
      trackEvent('Blog', 'Share', `Method: CopyLink, Post: ${post.title}`, post.id ? Number(post.id) : undefined);
      
      // Reset copy state after 2 seconds
      setTimeout(() => {
        setLinkCopied(false);
      }, 2000);
    });
  };

  // Rename the local function for Web Share API
  const triggerWebShare = async () => {
    if (!post) return;
    
    const numericId = post.id ? Number(post.id) || undefined : undefined;
    trackEvent('Blog', 'Share', `Method: WebShare, Post: ${post.title}`, numericId);
    
    // @ts-ignore - TS rule TS2774 is overly strict for this browser API check
    if (navigator.share) { 
      try {
        await navigator.share({
          title: post.title,
          text: post.meta_description,
          url: `https://www.periciatecnica.eng.br/blog/${post.slug}`,
        });
        trackEvent('Blog', 'ShareSuccess', `Method: WebShare, Post: ${post.title}`, numericId);
      } catch (error) {
        trackEvent('Blog', 'ShareError', `Method: WebShare, Post: ${post.title}`, numericId);
      }
    }
  };

  return (
    <div className="social-share-container">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <h4 className="social-share-title text-lg font-medium text-gray-900 dark:text-white mb-4 md:mb-0">
          Compartilhe este conhecimento:
        </h4>
        <div className="social-share-buttons">
          {/* Native share button (when available) */}
          {navigator.share && (
            <ShareTooltip text={tooltips.native} position="top">
              <button
                onClick={triggerWebShare}
                className="social-share-button native-share"
                aria-label="Compartilhar"
              >
                <Share2 size={20} className="text-gray-600 dark:text-gray-400" />
              </button>
            </ShareTooltip>
          )}
          
          {/* Copy link button */}
          <ShareTooltip text={tooltips.copyLink} position="top">
            <button
              onClick={handleCopyLink}
              className="social-share-button copy-link-share"
              aria-label="Copiar link do artigo"
            >
              {linkCopied ? (
                <Check size={20} className="text-green-600 dark:text-green-400" />
              ) : (
                <Link2 size={20} />
              )}
            </button>
          </ShareTooltip>
          
          {/* Social sharing buttons */}
          <ShareTooltip text={tooltips.facebook} position="top">
            <a 
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`https://www.periciatecnica.eng.br/blog/${post.slug}`)}&quote=${encodeURIComponent(facebookShareText)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="social-share-button facebook-share"
              aria-label="Compartilhar no Facebook"
              onClick={(e) => {
                e.preventDefault();
                handleSocialShare('Facebook');
                window.open(
                  `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`https://www.periciatecnica.eng.br/blog/${post.slug}`)}&quote=${encodeURIComponent(facebookShareText)}`,
                  'facebook-share-dialog',
                  'width=800,height=600'
                );
              }}
            >
              <Facebook size={20} />
            </a>
          </ShareTooltip>
          
          <ShareTooltip text={tooltips.twitter} position="top">
            <a 
              href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(`https://www.periciatecnica.eng.br/blog/${post.slug}`)}&text=${encodeURIComponent(twitterShareText)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="social-share-button twitter-share"
              aria-label="Compartilhar no X"
              onClick={(e) => {
                e.preventDefault();
                handleSocialShare('Twitter');
                window.open(
                  `https://twitter.com/intent/tweet?url=${encodeURIComponent(`https://www.periciatecnica.eng.br/blog/${post.slug}`)}&text=${encodeURIComponent(twitterShareText)}`,
                  'twitter-share-dialog',
                  'width=800,height=600'
                );
              }}
            >
              <X size={20} />
            </a>
          </ShareTooltip>
          
          <ShareTooltip text={tooltips.linkedin} position="top">
            <a 
              href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(`https://www.periciatecnica.eng.br/blog/${post.slug}`)}&title=${encodeURIComponent(post.title || '')}&summary=${encodeURIComponent(linkedinShareText)}&source=PericiaJudicial`}
              target="_blank"
              rel="noopener noreferrer"
              className="social-share-button linkedin-share"
              aria-label="Compartilhar no LinkedIn"
              onClick={(e) => {
                e.preventDefault();
                handleSocialShare('LinkedIn');
                window.open(
                  `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(`https://www.periciatecnica.eng.br/blog/${post.slug}`)}&title=${encodeURIComponent(post.title || '')}&summary=${encodeURIComponent(linkedinShareText)}&source=PericiaJudicial`,
                  'linkedin-share-dialog',
                  'width=800,height=600'
                );
              }}
            >
              <Linkedin size={20} />
            </a>
          </ShareTooltip>
          
          {/* WhatsApp sharing button */}
          <ShareTooltip text={tooltips.whatsapp} position="top">
            <a 
              href={`https://api.whatsapp.com/send/?text=${encodeURIComponent(whatsappShareText)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="social-share-button whatsapp-share"
              aria-label="Compartilhar no WhatsApp"
              onClick={(e) => {
                e.preventDefault();
                handleSocialShare('WhatsApp');
                window.open(
                  `https://api.whatsapp.com/send/?text=${encodeURIComponent(whatsappShareText)}`,
                  'whatsapp-share-dialog',
                  'width=800,height=600'
                );
              }}
            >
              {/* Official WhatsApp Icon - same as footer */}
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 24 24" 
                fill="currentColor" 
                className="whatsapp-icon"
                width="20"
                height="20"
              >
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
            </a>
          </ShareTooltip>
        </div>
      </div>
      
      {/* Share message */}
      <p className="share-message">
        Compartilhe este conteúdo com advogados, engenheiros e outros profissionais que possam se beneficiar desta informação técnica especializada.
      </p>
    </div>
  );
};

export default BlogPostSocialShare;
